import React from "react";

import { Page } from "ComparePlans/Page";
import { MedicalPlanMapping } from "Features/MedicalPlanMappings/medicalPlanMappings.types";
import { useHistory } from "react-router-dom";
import { useCurrentAndRenewalSuites } from "RenewalSuitesComparison/hooks";
import BasicLoading from "ZeroRuby/BasicLoading";

import { useRenewalSuitesComparisonContext } from "../Context";
import { baseURL } from "../urls";
import { ErrorHandler } from "Cobalt/ErrorHandler";
import { TitleSmall } from "Cobalt/Header";
import { Tab, Tabs } from "Cobalt/Tabs";
import { Grid } from "Common/Grid";
import { Div } from "Common/HTML";
import BodyText from "Theme/BodyText";
import { LinkButton } from "Theme/Button";
import { CardSection } from "Theme/Card";

const hydratePlanWithPremiums = ({ plan, premium, employeesEnrolledCount }) => {
  const averageEmployeeCost = employeesEnrolledCount ? premium / employeesEnrolledCount : null;
  const hydratedPlan = {
    ...plan,
    total_cost: premium,
    average_monthly_premium: averageEmployeeCost,
  };
  return hydratedPlan;
};

interface BenefitsComparisonSectionProps {
  medicalPlanMappings: MedicalPlanMapping[];
}
const BenefitsComparisonSection = ({ medicalPlanMappings }: BenefitsComparisonSectionProps) => {
  const { selectedMapping, setSelectedMapping } = useRenewalSuitesComparisonContext();
  const medicalPlanMapping = selectedMapping || medicalPlanMappings[0];
  const { currentSuite, error, isError, isLoading, renewalSuites } = useCurrentAndRenewalSuites({ medicalPlanMapping: medicalPlanMappings[0] });
  if (isLoading) {
    return <BasicLoading />;
  }
  if (isError) {
    return (
      <CardSection>
        <ErrorHandler name="RenewalSuitesComparison" action="Get current plan suite" isError={isError} error={error} />
      </CardSection>
    );
  }

  const employeesEnrolledCount = Object.values(currentSuite.currentEmployeeCosts).filter(({ planID }) => planID === medicalPlanMapping.planID).length;
  const hydratedRenewalPlans = medicalPlanMapping.items.map(({ planID, planSuiteID }) => {
    const renewalSuite = renewalSuites.find(({ id }) => id === planSuiteID);
    const plan = renewalSuite.plans.find(({ id }) => id === planID);
    const planPremium = renewalSuite.renewalPlanCosts[planID].premium;
    return hydratePlanWithPremiums({ plan, premium: planPremium, employeesEnrolledCount });
  });

  return (
    <>
      <Tabs
        defaultActiveKey={(medicalPlanMapping || medicalPlanMappings[0]).name}
        onSelect={(eventKey) => {
          setSelectedMapping(medicalPlanMappings.find(({ name }) => name === eventKey));
        }}
      >
        {/* eslint-disable-next-line prettier/prettier */}
        {medicalPlanMappings?.map((mapping) => (
          <Tab key={mapping.name} eventKey={mapping.name} title={mapping.name} tabClassName="bg-white" />
        ))}
      </Tabs>
      <Div>
        <Page plans={hydratedRenewalPlans} isSampleQuote={false} />
      </Div>
    </>
  );
};

type DetailedBenefitsComparisonPageProps = {
  medicalPlanMappings: MedicalPlanMapping[];
};

const DetailedBenefitsComparisonPage = ({ medicalPlanMappings }: DetailedBenefitsComparisonPageProps) => {
  const history = useHistory();
  return (
    <>
      <div className="bg-white">
        <Grid pb={6}>
          <LinkButton
            onClick={() => {
              history.push(baseURL);
            }}
            color="link"
            role="link"
            px={0}
            my={4}
          >
            Back
          </LinkButton>
          <TitleSmall mt={0} mb={5}>
            See How Benefits Compare For Each Renewal Option
          </TitleSmall>
          <BodyText>Select a tab below to see how the benefits of your current plan compare with each renewal option.</BodyText>
        </Grid>
      </div>
      <Grid py={6}>
        <BenefitsComparisonSection medicalPlanMappings={medicalPlanMappings} />
      </Grid>
    </>
  );
};

export default DetailedBenefitsComparisonPage;
